import * as clack from '@clack/prompts'
import fs from 'node:fs'
import path from 'node:path'
import type { SeedCommandOptions } from '@cli-types/core-engine/commands.js'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import { isInteractiveSession, spinner } from '@cli-utils/terminal.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace/resolve-admin-namespace.js'
import { resolveConfig } from '@next-adapter/config/resolve-config.js'
import { buildSeedScript } from './build-seed-script.js'

export async function runSeedCommand(options: SeedCommandOptions): Promise<void> {
  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()

  clack.intro('BetterStart Seed')

  let config: ResolvedBetterstartConfig
  try {
    config = await resolveConfig(cwd)
  } catch (err) {
    clack.cancel(`Error loading config: ${err instanceof Error ? err.message : String(err)}`)
    process.exit(1)
  }

  const adminDir = config.paths?.admin ?? './admin'
  const interactive = isInteractiveSession()

  let email: string
  if (options.email) {
    if (!options.email.includes('@')) {
      clack.log.error(`Invalid email "${options.email}".`)
      process.exit(1)
    }
    email = options.email
  } else if (!interactive) {
    clack.log.error('Provide --email to run seed without a terminal.')
    process.exit(1)
  } else {
    const emailInput = await clack.text({
      message: 'Admin email',
      placeholder: 'admin@example.com',
      validate: (v) => {
        if (!v || !v.includes('@')) return 'Please enter a valid email'
      }
    })
    if (clack.isCancel(emailInput)) {
      clack.cancel('Cancelled.')
      return
    }
    email = emailInput
  }

  const envPassword = process.env.BETTERSTART_ADMIN_PASSWORD
  let password: string
  if (envPassword) {
    if (envPassword.length < 8) {
      clack.log.error('BETTERSTART_ADMIN_PASSWORD must be at least 8 characters.')
      process.exit(1)
    }
    password = envPassword
  } else if (!interactive) {
    clack.log.error('Set BETTERSTART_ADMIN_PASSWORD to run seed without a terminal.')
    process.exit(1)
  } else {
    const passwordInput = await clack.password({
      message: 'Admin password',
      validate: (v) => {
        if (!v || v.length < 8) return 'Password must be at least 8 characters'
      }
    })
    if (clack.isCancel(passwordInput)) {
      clack.cancel('Cancelled.')
      return
    }
    password = passwordInput
  }

  let name: string
  if (options.name) {
    name = options.name
  } else if (!interactive) {
    name = 'Admin'
  } else {
    const nameInput = await clack.text({
      message: 'Admin name',
      placeholder: 'Admin',
      defaultValue: 'Admin'
    })
    if (clack.isCancel(nameInput)) {
      clack.cancel('Cancelled.')
      return
    }
    name = nameInput
  }

  const scriptsDir = path.join(cwd, adminDir, 'scripts')
  const seedPath = path.join(scriptsDir, 'seed.ts')

  if (!fs.existsSync(scriptsDir)) {
    fs.mkdirSync(scriptsDir, { recursive: true })
  }
  const namespace = resolveAdminNamespace(config.frameworkConfig.next.namespace)
  fs.writeFileSync(seedPath, buildSeedScript(`${namespace.apiPath}/auth`), 'utf-8')

  const { execFile } = await import('node:child_process')
  const tsxBin = path.join(cwd, 'node_modules', '.bin', 'tsx')

  const runSeed = (overwrite: boolean) =>
    new Promise<{ code: number; stdout: string }>((resolve, reject) => {
      execFile(
        tsxBin,
        [seedPath],
        {
          cwd,
          env: {
            ...process.env,
            SEED_EMAIL: email,
            SEED_PASSWORD: password,
            SEED_NAME: name || 'Admin',
            ...(overwrite ? { SEED_OVERWRITE_MODE: 'email' } : {})
          }
        },
        (err, stdout, stderr) => {
          if (err && 'code' in err && err.code === 2) {
            resolve({ code: 2, stdout })
          } else if (err) {
            reject(new Error(stderr || err.message))
          } else {
            resolve({ code: 0, stdout })
          }
        }
      )
    })

  const seedSpinner = spinner()
  seedSpinner.start('Creating admin user...')

  try {
    const result = await runSeed(false)

    if (result.code === 2) {
      const existingName =
        result.stdout
          .split('\n')
          .find((l) => l.startsWith('EXISTING_USER:'))
          ?.replace('EXISTING_USER:', '')
          ?.trim() || 'unknown'

      seedSpinner.stop(`Account already exists for ${email}`)

      if (!interactive) {
        clack.log.error(
          `An admin account (${existingName}) already exists for ${email}. Replacing it needs a terminal.`
        )
        try {
          fs.unlinkSync(seedPath)
        } catch {}
        process.exit(1)
      }

      const overwrite = await clack.confirm({
        message: `An admin account (${existingName}) already exists with this email. Replace it?`
      })
      if (clack.isCancel(overwrite) || !overwrite) {
        clack.cancel('Seed cancelled.')
        try {
          fs.unlinkSync(seedPath)
        } catch {}
        return
      }

      seedSpinner.start('Replacing admin user...')
      await runSeed(true)
      seedSpinner.stop('Admin user replaced')
    } else {
      seedSpinner.stop('Admin user created')
    }
  } catch (err) {
    seedSpinner.stop('Failed to create admin user')

    const errMsg = err instanceof Error ? err.message : String(err)
    clack.log.error(errMsg)
    clack.log.info('You can run the seed script manually:')
    clack.log.info(
      `  SEED_EMAIL="${email}" SEED_PASSWORD="..." npx tsx ${path.relative(cwd, seedPath)}`
    )
    clack.outro('')
    process.exit(1)
  }

  try {
    fs.unlinkSync(seedPath)
    if (fs.existsSync(scriptsDir) && fs.readdirSync(scriptsDir).length === 0) {
      fs.rmdirSync(scriptsDir)
    }
  } catch {}

  clack.outro(`Admin user ready: ${email}`)
}
